import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL;
const STEPS = ['Rango de fechas', 'Selección de recetas', 'Generar plan'];

const formatDay = (dateKey) => {
  try {
    return format(new Date(`${dateKey}T00:00:00`), "EEEE d MMM", { locale: es });
  } catch {
    return dateKey;
  }
};

const BatchCookingWizard = ({ initialRange, startAtSelection = false, source = 'menu-semanal', onCancel, onCreated }) => {
  const { authToken } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [start, setStart] = useState(initialRange?.start || '');
  const [end, setEnd] = useState(initialRange?.end || '');
  const [meals, setMeals] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loadingSelection, setLoadingSelection] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const selectableIds = useMemo(() => {
    const ids = new Set();
    for (const meal of meals) {
      if (meal.usesStock) continue;
      for (const recipe of meal.recipes || []) ids.add(recipe.id);
    }
    return ids;
  }, [meals]);

  const fetchSelection = useCallback(async () => {
    if (!authToken || !start || !end) return;
    setLoadingSelection(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/range-selection?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudo cargar el menú del rango');
      const loadedMeals = payload.data?.meals || [];
      setMeals(loadedMeals);
      // Pre-seleccionar todas las recetas que no salen de Fondo.
      const preselect = new Set();
      for (const meal of loadedMeals) {
        if (meal.usesStock) continue;
        for (const recipe of meal.recipes || []) preselect.add(recipe.id);
      }
      setSelectedIds(preselect);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSelection(false);
    }
  }, [authToken, start, end]);

  // Si llegamos directamente al paso de selección (desde el generador IA).
  useEffect(() => {
    if (startAtSelection && start && end) {
      setActiveStep(1);
    }
  }, [startAtSelection, start, end]);

  useEffect(() => {
    if (activeStep === 1) fetchSelection();
  }, [activeStep, fetchSelection]);

  const toggleRecipe = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(selectableIds));
  const selectNone = () => setSelectedIds(new Set());

  const handleGenerate = async () => {
    setActiveStep(2);
    setGenerating(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          start,
          end,
          selectedRecipeIds: Array.from(selectedIds),
          source,
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudo generar el plan');
      onCreated?.(payload.data.planId);
    } catch (err) {
      setError(err.message);
      setActiveStep(1);
    } finally {
      setGenerating(false);
    }
  };

  const mealsByDate = useMemo(() => {
    const grouped = {};
    for (const meal of meals) {
      if (!grouped[meal.date]) grouped[meal.date] = [];
      grouped[meal.date].push(meal);
    }
    return grouped;
  }, [meals]);

  const selectedCount = useMemo(
    () => Array.from(selectedIds).filter((id) => selectableIds.has(id)).length,
    [selectedIds, selectableIds],
  );

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {activeStep === 0 && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Elige el rango de fechas. El plan de batch cooking se generará con las recetas que ya tengas planificadas en esos días.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Desde"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Hasta"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <Box display="flex" justifyContent="space-between">
            <Button onClick={onCancel}>Cancelar</Button>
            <Button
              variant="contained"
              disabled={!start || !end || end < start}
              onClick={() => setActiveStep(1)}
            >
              Siguiente
            </Button>
          </Box>
        </Stack>
      )}

      {activeStep === 1 && (
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Selecciona las recetas que quieres incluir en el batch cooking. Las comidas que salen del Fondo se excluyen automáticamente.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<DoneAllIcon />} onClick={selectAll}>Optimizar todo</Button>
              <Button size="small" onClick={selectNone}>Ninguna</Button>
            </Stack>
          </Box>

          {loadingSelection ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : meals.length === 0 ? (
            <Alert severity="info">No hay recetas planificadas en este rango. Vuelve atrás y elige otro rango o añade recetas al menú.</Alert>
          ) : (
            <Stack spacing={2} sx={{ maxHeight: '50vh', overflow: 'auto' }}>
              {Object.entries(mealsByDate).map(([date, dayMeals]) => (
                <Paper key={date} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography fontWeight={800} sx={{ textTransform: 'capitalize' }}>{formatDay(date)}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.5}>
                    {dayMeals.map((meal) => (
                      <Box key={`${date}-${meal.meal}`}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>{meal.meal}</Typography>
                          {meal.usesStock && <Chip size="small" icon={<Inventory2Icon />} label="usa fondo" color="warning" variant="outlined" />}
                        </Box>
                        <Stack sx={{ pl: 1 }}>
                          {(meal.recipes || []).map((recipe) => (
                            <FormControlLabel
                              key={`${date}-${meal.meal}-${recipe.id}`}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!meal.usesStock && selectedIds.has(recipe.id)}
                                  disabled={meal.usesStock}
                                  onChange={() => toggleRecipe(recipe.id)}
                                />
                              }
                              label={<Typography variant="body2">{recipe.name}</Typography>}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button onClick={() => setActiveStep(0)}>Atrás</Button>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">{selectedCount} recetas seleccionadas</Typography>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                disabled={selectedCount === 0}
                onClick={handleGenerate}
              >
                Generar plan con IA
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}

      {activeStep === 2 && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={5} gap={2}>
          {generating ? (
            <>
              <CircularProgress />
              <Typography color="text.secondary">La IA está diseñando tu plan de batch cooking…</Typography>
            </>
          ) : (
            <Typography color="text.secondary">Plan generado.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BatchCookingWizard;
